import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router';

import type { MascotStatus } from '@cartwhl/common/api/getMascot';

import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';

import BatchComicJobErrorModal, {
  type BatchComicErrorNavigationJob,
} from '../../shared/BatchComicJobErrorModal';
import BatchMotionThumbnailLoadingChip from '../../shared/BatchMotionThumbnailLoadingChip';
import {
  BatchMotionJobThumbnailGrid,
  type BatchMotionThumbnailGridFile,
} from '../../shared/BatchMotionJobThumbnailGrid';
import BatchMascotVideoPreviewModal, {
  type MascotVideoPreviewNavigationJob,
} from '../../shared/BatchMascotVideoPreviewModal';
import { useStore, withProvider } from '../batchMascotProcessStore';
import type { MascotJobMetadata } from '../types';

/** Matches preview modal nav controls: secondary + hairline border. */
const OUTLINE_NAV_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

function mascotBatchStatusLabel(s: 'idle' | 'loading' | MascotStatus): string {
  if (s === 'idle' || s === 'loading') return 'Loading';
  const labels: Record<MascotStatus, string> = {
    VALIDATING: 'Validating',
    IN_PROGRESS: 'Processing',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    FAILED: 'Failed',
    REJECTED: 'Rejected',
  };
  return labels[s] ?? s;
}

function isFailedMascotStatus(status?: MascotJobMetadata['status']) {
  return (
    status === 'FAILED' || status === 'CANCELLED' || status === 'REJECTED'
  );
}

function isInFlightMascotStatus(status?: MascotJobMetadata['status']) {
  return status === 'VALIDATING' || status === 'IN_PROGRESS';
}

/** Map a MascotJobMetadata to the shared thumbnail grid's file shape. */
function toGridFile(file: MascotJobMetadata): BatchMotionThumbnailGridFile {
  return {
    id: file.id,
    name: file.name,
    status: isFailedMascotStatus(file.status)
      ? 'FAILED'
      : file.status === 'COMPLETED'
        ? 'COMPLETED'
        : undefined,
    errorMessage: file.errorMessage,
    studioPreviewURL: file.outputVideoURL,
  };
}

// ---------------------------------------------------------------------------
// Main ProcessingScreen
// ---------------------------------------------------------------------------

function ProcessingScreen() {
  const store = useStore();
  const navigate = useNavigate();
  const { id: processIdFromUrl } = useParams<{ id: string }>();
  const {
    fileMetadata,
    processName,
    processId,
    estimatedProcessingTime,
    status,
    downloadUrl,
  } = store.state;

  useEffect(() => {
    if (processIdFromUrl && processIdFromUrl !== processId) {
      store.loadProcess(processIdFromUrl);
    }
  }, [processIdFromUrl, processId, store]);

  useEffect(() => {
    return () => store.stopPolling();
  }, [store]);

  const includedFiles = useMemo(
    () =>
      fileMetadata.filter(
        (f) =>
          f.status === 'COMPLETED' ||
          f.status === 'VALIDATING' ||
          f.status === 'IN_PROGRESS',
      ),
    [fileMetadata],
  );
  const omittedFiles = useMemo(
    () => fileMetadata.filter((f) => isFailedMascotStatus(f.status)),
    [fileMetadata],
  );

  const hasJobFailures = omittedFiles.length > 0;
  const [fileFilter, setFileFilter] = useState<'completed' | 'errors'>(
    'completed',
  );

  const allFilesResolved = useMemo(
    () =>
      fileMetadata.length > 0 &&
      fileMetadata.every((f) => !isInFlightMascotStatus(f.status)),
    [fileMetadata],
  );

  const fullJobFailure = useMemo(
    () =>
      allFilesResolved &&
      fileMetadata.length > 0 &&
      includedFiles.length === 0 &&
      omittedFiles.length > 0,
    [
      allFilesResolved,
      fileMetadata.length,
      includedFiles.length,
      omittedFiles.length,
    ],
  );

  const errorsViewActive = useMemo(
    () => fullJobFailure || (hasJobFailures && fileFilter === 'errors'),
    [fullJobFailure, hasJobFailures, fileFilter],
  );

  useEffect(() => {
    if (fullJobFailure) setFileFilter('errors');
  }, [fullJobFailure]);

  useEffect(() => {
    if (fileFilter === 'errors') setSelectedJobIds(new Set());
  }, [fileFilter]);

  // Selection
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(
    () => new Set(),
  );
  const lastSelectedRef = useRef<string | null>(null);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);

  // Keep selection pruned to current metadata
  useEffect(() => {
    const valid = new Set(fileMetadata.map((f) => f.id));
    setSelectedJobIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (valid.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [fileMetadata]);

  const visibleFiles = errorsViewActive ? omittedFiles : includedFiles;
  const visibleGridFiles = useMemo(
    () => visibleFiles.map(toGridFile),
    [visibleFiles],
  );

  // Preview modal (completed output videos)
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const previewFile = useMemo(
    () => (previewJobId ? fileMetadata.find((f) => f.id === previewJobId) : undefined),
    [previewJobId, fileMetadata],
  );
  const previewNavigationJobs = useMemo<MascotVideoPreviewNavigationJob[]>(
    () =>
      includedFiles
        .filter((f) => f.status === 'COMPLETED' && f.outputVideoURL)
        .map((f, i) => ({ id: f.id, name: f.name, jobNumber: i + 1 })),
    [includedFiles],
  );
  const previewJobNumber = useMemo(
    () =>
      previewNavigationJobs.findIndex((j) => j.id === previewJobId) + 1,
    [previewNavigationJobs, previewJobId],
  );

  // Error modal (failed thumbnails)
  const [errorModalJobId, setErrorModalJobId] = useState<string | null>(null);
  const errorModalFile = useMemo(
    () => (errorModalJobId ? fileMetadata.find((f) => f.id === errorModalJobId) : undefined),
    [errorModalJobId, fileMetadata],
  );
  const errorNavigationJobs = useMemo<BatchComicErrorNavigationJob[]>(
    () =>
      omittedFiles.map((f, i) => ({
        id: f.id,
        name: f.name,
        batchMotionNumber: i + 1,
      })),
    [omittedFiles],
  );
  const errorModalJobNumber = useMemo(
    () => errorNavigationJobs.findIndex((j) => j.id === errorModalJobId) + 1,
    [errorNavigationJobs, errorModalJobId],
  );

  const handleCheckbox = useCallback(
    (jobId: string, event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const isShift = event.shiftKey;
      const orderedIds = visibleFiles.map((f) => f.id);
      setSelectedJobIds((prev) => {
        const next = new Set(prev);
        if (isShift) {
          const anchor = lastSelectedRef.current;
          if (
            anchor &&
            orderedIds.includes(anchor) &&
            orderedIds.includes(jobId)
          ) {
            const a = orderedIds.indexOf(anchor);
            const b = orderedIds.indexOf(jobId);
            const [lo, hi] = a <= b ? [a, b] : [b, a];
            for (let i = lo; i <= hi; i++) next.add(orderedIds[i]!);
          } else {
            next.add(jobId);
          }
        } else if (next.has(jobId)) {
          next.delete(jobId);
        } else {
          next.add(jobId);
        }
        return next;
      });
      lastSelectedRef.current = jobId;
    },
    [visibleFiles],
  );

  const handleDownloadJob = useCallback(
    (jobId: string, event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const file = fileMetadata.find((f) => f.id === jobId);
      if (!file) return;
      setDownloadingJobId(jobId);
      try {
        store.downloadOutputVideo(file);
      } finally {
        setDownloadingJobId(null);
      }
    },
    [fileMetadata, store],
  );

  const handleBulkDownload = useCallback(async () => {
    if (selectedJobIds.size === 0) return;
    setBulkDownloadLoading(true);
    try {
      for (const jobId of selectedJobIds) {
        const file = fileMetadata.find((f) => f.id === jobId);
        if (!file || file.status !== 'COMPLETED') continue;
        store.downloadOutputVideo(file);
        await new Promise((r) => setTimeout(r, 350));
      }
    } finally {
      setBulkDownloadLoading(false);
    }
  }, [selectedJobIds, fileMetadata, store]);

  const isLoading = status === 'loading' || status === 'idle';
  const isComplete = status === 'COMPLETED';
  const isError = status === 'FAILED';

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 60) return 'Less than 1 min';
    return `~${Math.ceil(seconds / 60)} min`;
  };

  const handleBackToHome = () => {
    store.reset();
    navigate('/mascot');
  };

  const headerTitle =
    processName?.trim() || (processId ? `Batch ${processId}` : 'Batch');
  const jobCount = fileMetadata.length;
  const showHeaderSpinner = fileMetadata.some((f) =>
    isInFlightMascotStatus(f.status),
  );

  const batchDetailsBoxClass = useMemo(() => {
    if (errorsViewActive) return 'bg-red/10 dark:bg-red/20';
    if (isComplete) return 'bg-green/10 dark:bg-green/20';
    if (isError) return 'bg-red/10 dark:bg-red/20';
    return 'bg-blue/10 dark:bg-blue/20';
  }, [errorsViewActive, isComplete, isError]);

  // ---------------------------------------------------------------------------
  // Loading / fatal error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <div className="container mx-auto px-4 pt-12 pb-6 sm:px-6 lg:px-8">
          <div className="flex min-h-96 flex-col items-center justify-center gap-4">
            <Loading className="text-green h-12 w-12" />
            <Text variant="studio/body/md" className="text-secondary">
              Loading process status...
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (isError && fileMetadata.length === 0) {
    return (
      <div className="flex flex-col">
        <div className="container mx-auto px-4 pt-12 pb-6 sm:px-6 lg:px-8">
          <div className="flex min-h-96 flex-col items-center justify-center gap-4">
            <WarningIcon className="text-red h-12 w-12" />
            <Text variant="studio/body/md" className="text-secondary">
              Failed to load process. The process ID may be invalid.
            </Text>
            <Button variant="secondary" onClick={handleBackToHome}>
              Back to Mascot
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main layout
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="flex flex-col">
        <div className="container mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="bg-secondary rounded-2xl p-4">
            <div className="bg-primary border-primary flex flex-col gap-4 rounded-lg border p-8">

              {/* ---- Header row: back button + title + count + actions ---- */}
              <div className="border-primary border-b pb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_NAV_BUTTON}`}
                      icon={<ChevronLeftIcon className="h-5 w-5" />}
                      aria-label="Back to Mascot home"
                      onClick={handleBackToHome}
                    />
                    <div className="text-secondary flex min-w-0 items-center gap-2 sm:gap-3">
                      <Text
                        as="h1"
                        variant="studio/body/medium/24"
                        className="text-primary min-w-0 max-w-[min(100%,20rem)] truncate sm:max-w-112"
                      >
                        {headerTitle}
                      </Text>
                      {showHeaderSpinner ? (
                        <BatchMotionThumbnailLoadingChip layout="inline" />
                      ) : null}
                      <Text
                        as="span"
                        variant="studio/body/medium/24"
                        className="text-secondary inline-flex shrink-0 items-baseline gap-1 whitespace-nowrap"
                      >
                        <span className="tabular-nums">{jobCount}</span>
                        <span>{jobCount === 1 ? 'scene' : 'scenes'}</span>
                      </Text>
                    </div>
                  </div>

                  {/* Right side: selection chip + bulk download / download all */}
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
                    {!errorsViewActive && selectedJobIds.size > 0 ? (
                      <div className="bg-blue/10 dark:bg-blue/20 flex items-center gap-2 rounded-2xl px-3 py-2">
                        <Button
                          type="button"
                          variant="tertiary"
                          size="sm"
                          icon={<CrossIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobIds(new Set());
                          }}
                          className="h-6 w-6 p-0"
                          aria-label="Clear selection"
                        />
                        <Text variant="studio/body/sm" className="text-primary">
                          {selectedJobIds.size} Selected
                        </Text>
                      </div>
                    ) : null}

                    {!errorsViewActive && selectedJobIds.size > 0 ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="lg"
                        alignIcon="end"
                        loading={bulkDownloadLoading}
                        icon={<DownloadIcon className="h-5 w-5" />}
                        onClick={() => void handleBulkDownload()}
                      >
                        Download selected
                      </Button>
                    ) : isComplete && downloadUrl ? (
                      <a
                        href={downloadUrl}
                        download
                        className="inline-flex shrink-0 no-underline"
                      >
                        <Button
                          variant="primary"
                          size="lg"
                          alignIcon="end"
                          icon={<DownloadIcon className="h-5 w-5" />}
                        >
                          Download the batch
                        </Button>
                      </a>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        alignIcon="end"
                        disabled
                        icon={<DownloadIcon className="h-5 w-5" />}
                      >
                        Download the batch
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* ---- Completed / Errors tabs ---- */}
              {hasJobFailures && !fullJobFailure ? (
                <div
                  className="inline-flex w-fit max-w-full flex-wrap gap-2"
                  role="tablist"
                  aria-label="Show completed or failed files"
                >
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={fileFilter === 'completed'}
                    variant={fileFilter === 'completed' ? 'secondary' : 'tertiary'}
                    size="lg"
                    icon={<CheckmarkIcon className="text-green" />}
                    onClick={() => setFileFilter('completed')}
                  >
                    Completed scenes ({includedFiles.length})
                  </Button>
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={fileFilter === 'errors'}
                    variant={fileFilter === 'errors' ? 'secondary' : 'tertiary'}
                    size="lg"
                    icon={<CrossIcon className="text-red" />}
                    onClick={() => setFileFilter('errors')}
                  >
                    Errors ({omittedFiles.length})
                  </Button>
                </div>
              ) : null}

              {/* ---- Batch details card ---- */}
              <div className={`rounded-lg p-8 ${batchDetailsBoxClass}`}>
                <div className="grid grid-cols-2 items-start gap-x-4 gap-y-8 sm:gap-x-12 lg:gap-x-20">
                  {errorsViewActive ? (
                    <>
                      <div className="flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Status
                        </Text>
                        <Text variant="studio/body/medium/md" className="text-primary">
                          Error
                        </Text>
                      </div>
                      <div className="flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Output format
                        </Text>
                        <Text variant="studio/body/medium/md" className="text-primary">
                          MP4
                        </Text>
                      </div>
                      <div className="col-span-2 flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Mascot batch ID
                        </Text>
                        <div className="flex min-w-0 w-full max-w-full items-start gap-2 overflow-hidden">
                          <Button
                            variant="tertiary"
                            icon={<CopyIcon className="h-6 w-6" />}
                            alignIcon="end"
                            onClick={() => store.copyProcessIdToClipboard()}
                            className="text-blue hover:text-blue-60 ml-0 min-w-0 max-w-full transition-colors sm:ml-[-16px]"
                            title="Copy to clipboard"
                          >
                            <Text variant="studio/body/medium/md" className="text-primary min-w-0 truncate">
                              {processId}
                            </Text>
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Status
                        </Text>
                        <Text variant="studio/body/medium/md" className="text-primary">
                          {mascotBatchStatusLabel(status)}
                        </Text>
                      </div>
                      <div className="flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Output format
                        </Text>
                        <Text variant="studio/body/medium/md" className="text-primary">
                          MP4
                        </Text>
                      </div>
                      <div className="col-span-2 flex min-w-0 flex-col gap-2">
                        <Text variant="studio/body/md" className="text-secondary">
                          Mascot batch ID
                        </Text>
                        <div className="flex min-w-0 w-full max-w-full items-start gap-2 overflow-hidden">
                          <Button
                            variant="tertiary"
                            icon={<CopyIcon className="h-6 w-6" />}
                            alignIcon="end"
                            onClick={() => store.copyProcessIdToClipboard()}
                            className="text-blue hover:text-blue-60 ml-0 min-w-0 max-w-full transition-colors sm:ml-[-16px]"
                            title="Copy to clipboard"
                          >
                            <Text variant="studio/body/medium/md" className="text-primary min-w-0 truncate">
                              {processId}
                            </Text>
                          </Button>
                        </div>
                      </div>
                      {!isComplete && !isError ? (
                        <div className="flex min-w-0 flex-col gap-2">
                          <Text variant="studio/body/md" className="text-secondary">
                            Estimated time of completion
                          </Text>
                          <Text variant="studio/body/medium/md" className="text-primary">
                            {formatTime(estimatedProcessingTime)}
                          </Text>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              {/* ---- Thumbnail grid ---- */}
              <BatchMotionJobThumbnailGrid
                files={visibleGridFiles}
                onPreviewJob={(jobId) => setPreviewJobId(jobId)}
                canPreviewFile={(f) =>
                  f.status === 'COMPLETED' && Boolean(f.studioPreviewURL)
                }
                allowErrorExpand={false}
                selectionEnabled={!errorsViewActive}
                selectedJobIds={errorsViewActive ? new Set() : selectedJobIds}
                onCheckboxClick={errorsViewActive ? undefined : handleCheckbox}
                onDownloadJob={errorsViewActive ? undefined : handleDownloadJob}
                downloadingJobId={downloadingJobId}
                errorThumbnailVariant={errorsViewActive ? 'comic-errors' : 'default'}
                onErrorThumbnailClick={
                  errorsViewActive ? (id) => setErrorModalJobId(id) : undefined
                }
              />

            </div>
          </div>
        </div>
      </div>

      {/* ---- Output video preview modal ---- */}
      <BatchMascotVideoPreviewModal
        open={Boolean(previewJobId && previewFile?.outputVideoURL)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPreviewJobId(null);
        }}
        jobId={previewFile?.id ?? ''}
        jobTitle={previewFile?.name ?? ''}
        jobNumber={previewJobNumber}
        totalJobs={previewNavigationJobs.length}
        videoUrl={previewFile?.outputVideoURL ?? ''}
        onDownload={() => {
          if (previewFile) store.downloadOutputVideo(previewFile);
        }}
        navigation={{
          jobs: previewNavigationJobs,
          onSelectJobId: (id) => setPreviewJobId(id),
        }}
      />

      {/* ---- Error detail modal ---- */}
      {errorModalFile ? (
        <BatchComicJobErrorModal
          open={Boolean(errorModalJobId)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setErrorModalJobId(null);
          }}
          jobId={errorModalFile.id}
          jobTitle={errorModalFile.name}
          batchMotionNumber={errorModalJobNumber}
          totalMotionsInBatch={omittedFiles.length}
          errorMessage={
            errorModalFile.errorMessage?.trim() ||
            'No error message was provided.'
          }
          navigation={{
            jobs: errorNavigationJobs,
            onSelectJobId: (id) => setErrorModalJobId(id),
          }}
        />
      ) : null}
    </>
  );
}

export const ProcessingScreenWithStore = withProvider(ProcessingScreen);
export default ProcessingScreen;
